import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../utils/cn';
import {
  TOOLTIP_ARROW,
  type TooltipArrow
} from './styles';
import { TopArrow, BottomArrow, LeftArrow, RightArrow } from './svg';
import colors from '../../src/themes/default/colors';
import './Tooltip.css';

export interface TooltipProps {
  /**
   * Element that triggers the tooltip
   */
  children: React.ReactNode;
  /**
   * Tooltip content
   */
  content: React.ReactNode;
  /**
   * Tooltip theme
   */
  theme?: 'light' | 'dark' | null;
  /**
   * Arrow direction
   */
  arrow?: TooltipArrow;
  /**
   * Tooltip side (overrides arrow-based side if provided)
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Tooltip alignment (overrides arrow-based align if provided)
   */
  align?: 'start' | 'center' | 'end';
  /**
   * Whether the tooltip is shown (controlled)
   */
  open?: boolean;
  /**
   * Whether the tooltip is shown by default (uncontrolled)
   */
  defaultOpen?: boolean;
  /**
   * Delay before showing tooltip (ms)
   */
  delayDuration?: number;
  /**
   * Test ID for testing
   */
  dataTestId?: string;
  /**
   * Distance between tooltip and trigger (px)
   */
  sideOffset?: number;
  /**
   * Horizontal offset to align tooltip (px)
   */
  alignOffset?: number;
  /**
   * Animation duration in milliseconds (default: 300)
   */
  animationDuration?: number;
}

/**
 * Tooltip Component
 *
 * Displays explanatory text when hovering over or focusing on an element.
 */
export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  theme = 'light',
  arrow = TOOLTIP_ARROW.BOTTOM_CENTER,
  side: sideProp,
  align: alignProp,
  open,
  defaultOpen = false,
  delayDuration = 0,
  dataTestId,
  sideOffset = 10,
  alignOffset = 0,
  animationDuration
}) => {
  // Convert arrow direction to Radix UI side and align
  const getSideAndAlign = (arrowDir: TooltipArrow): { side: 'top' | 'right' | 'bottom' | 'left'; align: 'start' | 'center' | 'end' } => {
    if (arrowDir === TOOLTIP_ARROW.NONE) return { side: 'top', align: 'center' };
    
    if (arrowDir.startsWith('top-')) {
      return {
        side: 'top',
        align: arrowDir === TOOLTIP_ARROW.TOP_LEFT ? 'start' : arrowDir === TOOLTIP_ARROW.TOP_RIGHT ? 'end' : 'center'
      };
    }
    if (arrowDir.startsWith('bottom-')) {
      return {
        side: 'bottom',
        align: arrowDir === TOOLTIP_ARROW.BOTTOM_LEFT ? 'start' : arrowDir === TOOLTIP_ARROW.BOTTOM_RIGHT ? 'end' : 'center'
      };
    }
    if (arrowDir === TOOLTIP_ARROW.LEFT) return { side: 'left', align: 'center' };
    if (arrowDir === TOOLTIP_ARROW.RIGHT) return { side: 'right', align: 'center' };
    
    return { side: 'bottom', align: 'center' };
  };

  const { side: arrowSide, align: arrowAlign } = getSideAndAlign(arrow);
  const side = sideProp || arrowSide;
  const align = alignProp || arrowAlign;
  const showArrow = arrow !== TOOLTIP_ARROW.NONE;

  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root open={open} defaultOpen={defaultOpen}>
        <TooltipPrimitive.Trigger asChild>
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            align={align}
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            className="z-50"
            data-testid={dataTestId}
            {...(animationDuration != null && {
              style: { '--tooltip-animation-duration': `${animationDuration}ms` } as React.CSSProperties
            })}
          >
            {/* Wrapper with drop-shadow filter - applies to entire shape including arrow */}
            <div className="relative overflow-visible drop-shadow-[0px_1px_4px_rgba(26,32,34,0.15)]">
              {/* Content box with padding and border radius */}
              <div className={cn(
                'rounded-sm px-s py-2xs font-caption text-xs leading-xs whitespace-pre-line',
                'max-w-[250px]',
                theme === 'dark' ? 'bg-invert text-invert' : 'bg-primary text-primary'
              )}>
                {content}
              </div>
              
              {/* Arrow - Custom positioned SVG */}
              {showArrow && (
                <div 
                  className={cn(
                    'absolute',
                    // Top arrows
                    arrow === TOOLTIP_ARROW.TOP_CENTER && '-bottom-[5px] left-1/2 -translate-x-1/2',
                    arrow === TOOLTIP_ARROW.TOP_LEFT && '-bottom-[5px] left-m',
                    arrow === TOOLTIP_ARROW.TOP_RIGHT && '-bottom-[5px] right-m',
                    // Bottom arrows
                    arrow === TOOLTIP_ARROW.BOTTOM_CENTER && '-top-[5px] left-1/2 -translate-x-1/2',
                    arrow === TOOLTIP_ARROW.BOTTOM_LEFT && '-top-[5px] left-m',
                    arrow === TOOLTIP_ARROW.BOTTOM_RIGHT && '-top-[5px] right-m',
                    // Left/Right arrows
                    arrow === TOOLTIP_ARROW.LEFT && '-right-[5px] top-1/2 -translate-y-1/2',
                    arrow === TOOLTIP_ARROW.RIGHT && '-left-[5px] top-1/2 -translate-y-1/2'
                  )}
                  data-testid="tooltip-arrow"
                  data-fill={theme === 'dark' ? colors.gray[800] : colors.base.white}
                >
                  {arrow?.startsWith('top') && <TopArrow fill={theme === 'dark' ? colors.gray[800] : colors.base.white} />}
                  {arrow?.startsWith('bottom') && <BottomArrow fill={theme === 'dark' ? colors.gray[800] : colors.base.white} />}
                  {arrow === TOOLTIP_ARROW.LEFT && <LeftArrow fill={theme === 'dark' ? colors.gray[800] : colors.base.white} />}
                  {arrow === TOOLTIP_ARROW.RIGHT && <RightArrow fill={theme === 'dark' ? colors.gray[800] : colors.base.white} />}
                </div>
              )}
            </div>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};

Tooltip.displayName = 'Tooltip';
